import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Viewer, Worker, ScrollMode } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import { 
  scrollModePlugin,
  HorizontalScrollingIcon,
  VerticalScrollingIcon
} from '@react-pdf-viewer/scroll-mode';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import './BrochureViewer.css';

const BrochureViewer: React.FC = () => {
  const { projectName } = useParams<{ projectName: string }>();
  const [currentScrollMode, setCurrentScrollMode] = useState<ScrollMode>(ScrollMode.Horizontal);
  
  // Create plugin instances
  const scrollModePluginInstance = scrollModePlugin();
  const { switchScrollMode } = scrollModePluginInstance;
  
  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    sidebarTabs: (defaultTabs) => [
      defaultTabs[0], // Thumbnail tab
    ],
  });

  // Handle scroll mode change
  const handleScrollModeChange = (mode: ScrollMode) => {
    setCurrentScrollMode(mode);
    switchScrollMode(mode);
  };

  // Scroll mode options (only Vertical and Horizontal)
  const scrollModeOptions = [
    {
      mode: ScrollMode.Vertical,
      icon: <VerticalScrollingIcon />,
      label: 'Vertical',
      description: 'Scroll pages vertically'
    },
    {
      mode: ScrollMode.Horizontal,
      icon: <HorizontalScrollingIcon />,
      label: 'Horizontal',
      description: 'Scroll pages horizontally'
    }
  ];

  const brochureData: Record<string, any> = {
    antares: {
      name: 'ANTARES II',
      subtitle: 'DE CAYALÁ',
      color: '#98281F',
      backgroundColor: '#FFFFFF',
      description: 'Un desarrollo residencial exclusivo que combina elegancia y modernidad en el corazón de Cayalá.',
      pdfUrl: '/pdfs/antares.pdf'
    },
    nogales: {
      name: 'NOGALES',
      subtitle: 'DE CAYALÁ',
      phase: 'FASE III',
      color: '#7B8F47',
      backgroundColor: '#4A5D23',
      description: 'Espacios verdes y arquitectura sostenible definen este proyecto único en Cayalá.',
      pdfUrl: '/pdfs/nogales.pdf'
    },
    eonia: {
      name: 'EONIA',
      subtitle: 'DE CAYALÁ',
      color: '#4A90E2',
      backgroundColor: '#FFFFFF',
      description: 'Innovación y confort se encuentran en este desarrollo de vanguardia.',
      pdfUrl: '/pdfs/eonia.pdf'
    },
    azalea: {
      name: 'AZALEA',
      subtitle: 'DE CAYALÁ',
      color: '#801434',
      backgroundColor: '#8B1538',
      description: 'Lujo y sofisticación en cada detalle de este exclusivo proyecto residencial.',
      pdfUrl: '/pdfs/azalea.pdf'
    },
    cayala: {
      name: 'CAYALÁ',
      subtitle: 'CIUDAD URBANISTA',
      color: '#8B4513',
      backgroundColor: '#FFFFFF',
      description: 'La primera ciudad urbanista de Guatemala, donde la vida urbana se reinventa.',
      pdfUrl: '/pdfs/cayala-example.pdf'
    }
  };

  const project = brochureData[projectName || ''];

  if (!project) {
    return (
      <div className="brochure-viewer">
        <div className="brochure-not-found">
          <h2>Brochure no encontrado</h2>
          <p>El proyecto "{projectName}" no existe o no está disponible.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="brochure-viewer">
      {/* Custom Scroll Mode Controls */}
      <div className="scroll-mode-controls" style={{
        padding: '10px',
        backgroundColor: '#f5f5f5',
        borderBottom: '1px solid #ddd',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        flexWrap: 'wrap'
      }}>
        <span style={{ fontWeight: 'bold', marginRight: '10px' }}>Mode:</span>
        {scrollModeOptions.map((option) => (
          <button
            key={option.mode}
            onClick={() => handleScrollModeChange(option.mode)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              padding: '8px 12px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              backgroundColor: currentScrollMode === option.mode ? '#007bff' : '#fff',
              color: currentScrollMode === option.mode ? '#fff' : '#333',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'all 0.2s ease'
            }}
            title={option.description}
          >
            <span style={{ width: '16px', height: '16px', display: 'flex', alignItems: 'center' }}>
              {option.icon}
            </span>
            {option.label}
          </button>
        ))}
      </div>
      
      <div className="pdf-container" style={{ height: 'calc(100vh - 60px)' }}>
        <Worker workerUrl="/pdf.worker.min.js">
          <Viewer
            fileUrl={project.pdfUrl}
            plugins={[defaultLayoutPluginInstance, scrollModePluginInstance]}
            defaultScale={1}
            scrollMode={currentScrollMode}
          />
        </Worker>
      </div>
    </div>
  );
};

export default BrochureViewer;
